"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.HttpOptimization = void 0;
class HttpOptimization {
    constructor(cache) {
        this.inFlightAwaitTTl = 1000;
        this.cache = cache;
    }
    /**
     * Prevent concurrent calls to the same function with the same arguments to avoid rate limiting.
     * @param fn The function to manage concurrency.
     * @param resultCacheKey The cache key to use.
     * @returns A managed version of the function.
     */
    managedConcurrency(fn, resultCacheKey) {
        const inFlightRequests = {};
        return async (inFlighKey, ...args) => {
            const inFlightKey = HttpOptimization.generateInFlightKey(inFlighKey);
            if (!(inFlightKey in inFlightRequests)) {
                inFlightRequests[inFlightKey] = (async () => {
                    while (await this.isInFlight(inFlightKey)) {
                        // eslint-disable-next-line no-promise-executor-return
                        await new Promise((resolve) => setTimeout(resolve, this.inFlightAwaitTTl));
                    }
                    await this.setInFlightFlag(inFlightKey);
                    try {
                        // Check for a cached result
                        let cachedResult;
                        if (resultCacheKey) {
                            cachedResult = await this.cache.get(resultCacheKey);
                        }
                        // If no cached result, execute the function
                        if (!cachedResult) {
                            const freshResult = await fn(...args);
                            return freshResult;
                        }
                        return cachedResult;
                    }
                    catch (error) {
                        await this.clearInFlightFlag(inFlightKey);
                        delete inFlightRequests[inFlightKey]; // Clean up the in-flight request
                        throw error;
                    }
                    finally {
                        await this.clearInFlightFlag(inFlightKey);
                        delete inFlightRequests[inFlightKey]; // Clean up the in-flight request
                    }
                })();
            }
            return inFlightRequests[inFlightKey];
        };
    }
    /**
     * Sets the in-flight flag for a request.
     * @param key The key to set.
     * @param ttl The time-to-live for the key.
     */
    async setInFlightFlag(key, ttl = 10) {
        await this.cache.set(key, { inFlight: true }, { ttl });
    }
    /**
     * Checks if a request is in-flight.
     * @param key The key to check.
     * @returns Whether the key is in-flight.
     */
    async isInFlight(key) {
        const value = await this.cache.get(key);
        return !!value;
    }
    /**
     * Generates a key for in-flight cache.
     * @param inFlighKey A unique key used to generate a cache key.
     * @returns The generated key.
     */
    static generateInFlightKey(inFlighKey) {
        return `${inFlighKey}-inFlight`;
    }
    /**
     * Manually clears the in-flight flag from cache.
     * @param key The key to clear from the cache.
     * @returns The number of entries deleted.
     */
    async clearInFlightFlag(key) {
        return this.cache.del(key);
    }
}
exports.HttpOptimization = HttpOptimization;
//# sourceMappingURL=optimization.js.map